import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, User as AuthUser } from '@/services/auth-service';
import { UserRole } from '@/features/users/types';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permissionId: string) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session and validate token
    const checkAuth = async () => {
      try {
        if (AuthService.isAuthenticated()) {
          // Validate the stored token
          const isValid = await AuthService.validateToken();
          if (isValid) {
            // Get fresh user data
            const currentUser = await AuthService.getCurrentUser();
            setUser(currentUser);
          } else {
            // Token is invalid, clear auth
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        // Clear any invalid session data
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await AuthService.login({ username, password });
      // Convert LoginResponse user to full User object
      const fullUser: AuthUser = {
        ...response.user,
        created_at: Date.now() // Add missing created_at field
      };
      setUser(fullUser);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await AuthService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Always clear local state even if API call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      await AuthService.refreshToken();
      // Get updated user data after token refresh
      const currentUser = await AuthService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, clear auth state
      setUser(null);
      throw error;
    }
  };

  const hasPermission = (permissionId: string): boolean => {
    return AuthService.hasPermission(permissionId);
  };

  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    
    // Convert numeric role to string role for comparison
    const userRoleName = user.role_name?.toLowerCase() as UserRole;
    
    if (Array.isArray(role)) {
      return role.includes(userRoleName);
    }
    
    return userRoleName === role;
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasPermission,
    hasRole,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
